import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecurityHeaders } from '@/components/SecurityHeaders';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import {
  Users,
  Package,
  FolderTree,
  ShoppingCart,
  MessageSquare,
  Search,
  LogOut,
  TrendingUp,
  ExternalLink,
  Settings
} from 'lucide-react';

// Import admin modules
import UsersModule from '@/components/admin/UsersModule';
import { useAdminStats } from '@/hooks/useAdminStats';
import ProductsModule from '@/components/admin/ProductsModule';
import CategoriesModule from '@/components/admin/CategoriesModule';
import OrdersModule from '@/components/admin/OrdersModule';
import ContactManagement from '@/components/admin/ContactManagement';
import SiteSettingsModule from '@/components/admin/SiteSettingsModule';
import PageManagerModule from '@/components/admin/PageManagerModule';
import GradeManagementModule from '@/components/admin/GradeManagementModule';
import CitiesManagementModule from '@/components/admin/CitiesManagementModule';
import SupplierManager from '@/components/admin/SupplierManager';
// Direct update script import removed - images now managed manually only

const AdminDashboard = () => {
  const { user, profile, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const { stats, loading } = useAdminStats();

  const adminModules = [
    {
      id: 'users',
      title: 'ניהול משתמשים',
      description: 'צפייה ועריכת פרטי משתמשים',
      icon: Users,
      count: `${stats.totalUsers} משתמשים`
    },
    {
      id: 'products',
      title: 'ניהול מוצרים',
      description: 'הוספה, עריכה ומחיקת מוצרים',
      icon: Package,
      count: `${stats.totalProducts} מוצרים`
    },
    {
      id: 'categories',
      title: 'ניהול קטגוריות',
      description: 'ניהול קטגוריות ותת-קטגוריות',
      icon: FolderTree,
      count: `${stats.totalCategories} קטגוריות`
    },
    {
      id: 'orders',
      title: 'ניהול הזמנות',
      description: 'צפייה ועדכון סטטוס הזמנות',
      icon: ShoppingCart,
      count: `${stats.totalOrders} הזמנות`
    },
    {
      id: 'contact',
      title: 'יצירת קשר ופניות',
      description: 'ניהול הצעות מחיר, חיפוש מוצרים ופניות קשר',
      icon: MessageSquare,
      count: `${stats.pendingQuotes + stats.pendingSpecialRequests + stats.pendingContactRequests} פניות ממתינות`
    }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">סקירה כללית</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adminModules.map((module) => {
                const IconComponent = module.icon;
                return (
                  <Card
                    key={module.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => setSearchParams({ tab: module.id })}
                  >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium text-right">
                        {module.title}
                      </CardTitle>
                      <IconComponent className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-primary mb-1 text-right">
                        {loading ? '...' : module.count}
                      </div>
                      <p className="text-xs text-muted-foreground text-right">
                        {module.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      case 'users':
        return <UsersModule />;
      case 'products':
        return <ProductsModule />;
      case 'categories':
        return <CategoriesModule />;
      case 'suppliers':
        return <SupplierManager />;
      case 'grades':
        return <GradeManagementModule />;
      case 'orders':
        return <OrdersModule />;
      case 'contact':
        return <ContactManagement />;
      case 'pages':
        return <PageManagerModule />;
      case 'settings':
        return <SiteSettingsModule />;
      case 'cities':
        return <CitiesManagementModule />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SecurityHeaders isAdminPage={true} preventIndexing={true} />
      <SidebarProvider>
        <div className="flex min-h-screen w-full flex-col">
          {/* Mobile Header with Hamburger Menu */}
          <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 lg:hidden">
            <div className="container flex h-14 items-center px-4" dir="rtl">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 text-center">
                <h1 className="text-lg font-semibold">לוח ניהול</h1>
              </div>
            </div>
          </header>

          <div className="flex flex-1 w-full">
            <AdminSidebar stats={stats} loading={loading} profile={profile} />

            <SidebarInset className="flex-1 overflow-hidden">
              <div className="h-full px-4 md:px-8 py-4 overflow-y-auto">
                {/* Content */}
                <div className="text-right" dir="rtl">
                  {renderContent()}
                </div>
              </div>
            </SidebarInset>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default AdminDashboard;